import { useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useSendPackLink, useTeamRecipients } from "@/hooks/activation";
import { ActivationFunnelService } from "@/services/analytics/ActivationFunnelService";
import {
  clearEmailCampaign,
  type EmailCampaignIdentity,
  getOrCreateEmailCampaign,
  loadPendingEmailCampaign,
} from "@/services/activation/emailCampaignId";

interface SendLinkToTeamProps {
  packId: string;
  kind: "presentation" | "app";
  /** The Genie this pack points at. Null for a portal-scoped pack. */
  genieId?: string | null;
  brand?: string;
}

/**
 * One-click "send to team" — pre-selects everyone on the caller's team so
 * the default action is just hitting Send, no typing required.
 */
export const SendLinkToTeam = ({
  packId,
  kind,
  genieId = null,
  brand = "#4E9CFF",
}: SendLinkToTeamProps) => {
  const { members } = useTeamRecipients();
  const sendLink = useSendPackLink();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);
  const [lastResult, setLastResult] = useState<{
    unknown: number;
    notSent: number;
  } | null>(null);
  const [isRestoringCampaign, setIsRestoringCampaign] = useState(true);
  const campaignRef = useRef<EmailCampaignIdentity | null>(null);
  const campaignScope = `pack-link:${packId}:${kind}`;

  useEffect(() => {
    let active = true;
    campaignRef.current = null;
    setLastResult(null);
    setIsRestoringCampaign(true);
    void loadPendingEmailCampaign(campaignScope)
      .then((campaign) => {
        if (!active || !campaign) return;
        campaignRef.current = campaign;
        setSelected(new Set(campaign.recipients));
        setSeeded(true);
      })
      .finally(() => {
        if (active) setIsRestoringCampaign(false);
      });
    return () => {
      active = false;
    };
  }, [campaignScope]);

  useEffect(() => {
    if (seeded || members.length === 0) return;
    setSelected(new Set(members.map((member) => member.email!.toLowerCase())));
    setSeeded(true);
  }, [members, seeded]);

  if (members.length === 0) return null;

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      setLastResult(null);
      return next;
    });
  };

  const emails = [...selected];

  const handleSend = async () => {
    if (emails.length === 0) return;
    const fingerprint = [campaignScope, ...emails.sort()].join("\n");
    if (
      campaignRef.current &&
      campaignRef.current.fingerprint !== fingerprint
    ) {
      const replace = globalThis.confirm(
        "A previous link campaign is still pending. Replacing it may duplicate recipients whose delivery could not be confirmed. Start a new send with this edited list?",
      );
      if (!replace) return;
      clearEmailCampaign(campaignRef.current);
      campaignRef.current = null;
    }
    if (campaignRef.current?.fingerprint !== fingerprint) {
      campaignRef.current = await getOrCreateEmailCampaign(
        campaignScope,
        emails,
      );
    }
    const campaign = campaignRef.current;
    sendLink.mutate(
      { packId, emails, kind, campaignId: campaign.id },
      {
        onSuccess: (results) => {
          const sent = results.filter((r) => r.sent).length;
          const unknown = results.filter((r) => r.state === "unknown").length;
          const notSent = results.filter((r) => r.state === "not_sent").length;
          setLastResult({ unknown, notSent });
          // Activation funnel — a pack link reached someone else's inbox.
          // Only count confirmed deliveries: an `unknown` result may never
          // have left, and a funnel step must not claim more than happened.
          if (sent > 0) {
            ActivationFunnelService.packShared(genieId, "email", {
              link: kind,
              recipients: sent,
            });
          }
          if (unknown > 0) {
            toast.warning(
              `${sent} sent; ${unknown} unknown and locked against duplicate sends`,
            );
          } else if (notSent > 0) {
            toast.error(
              `${notSent} known not sent. Retry safely to send only those recipients.`,
            );
          } else {
            toast.success(sent === 1 ? "Sent" : `Sent to ${sent} people`);
            clearEmailCampaign(campaign);
            if (campaignRef.current?.id === campaign.id)
              campaignRef.current = null;
            setLastResult(null);
          }
        },
        onError: () =>
          toast.error("Couldn't confirm the send. Retry safely to resume it."),
      },
    );
  };

  const handleStartNewCampaign = () => {
    if (
      lastResult?.unknown &&
      !globalThis.confirm(
        "Some delivery results are unknown. Starting a new send may create duplicate emails. Continue?",
      )
    )
      return;
    if (campaignRef.current) clearEmailCampaign(campaignRef.current);
    campaignRef.current = null;
    setLastResult(null);
    toast.info("Ready to start a new send");
  };

  return (
    <div>
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
        Send to your team
      </label>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {members.map((member) => {
          const email = member.email!.toLowerCase();
          const isOn = selected.has(email);
          return (
            <button
              key={member.id}
              type="button"
              disabled={isRestoringCampaign}
              onClick={() => toggle(email)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                isOn
                  ? "text-white border-transparent"
                  : "text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
              style={
                isOn
                  ? { background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }
                  : undefined
              }
            >
              {member.full_name || member.email}
            </button>
          );
        })}
      </div>
      <Button
        onClick={handleSend}
        disabled={
          emails.length === 0 || isRestoringCampaign || sendLink.isPending
        }
        size="sm"
        className="text-white border-0"
        style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
      >
        {sendLink.isPending ? (
          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
        ) : (
          <Send className="w-4 h-4 mr-1.5" />
        )}
        {isRestoringCampaign
          ? "Restoring…"
          : sendLink.isPending
            ? "Sending…"
            : `Send${emails.length ? ` (${emails.length})` : ""}`}
      </Button>
      {lastResult?.unknown ? (
        <div className="mt-2 text-xs text-amber-700">
          Unknown deliveries stay locked against retries.{" "}
          <button
            type="button"
            className="underline"
            onClick={handleStartNewCampaign}
          >
            Start a new send (may duplicate)
          </button>
        </div>
      ) : null}
    </div>
  );
};
